import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { getLeaderBoard } from "../../../api/user";
import {
  ChevronRightIcon,
  AvatarPersonIcon,
  InfoCircleIcon,
} from "../landing/icons";
import {
  assetLabel,
  assetTypeLabel,
  competitionCopy,
  endMomentLabel,
  joinAfterStartShort,
  leagueTypeShort,
  portfolioVisibilityGameShort,
  portfolioVisibilityShort,
  startingCashLabel,
  startMomentLabel,
  tradeLimitLabel,
} from "../landing/leagueFormat";
import { memberCount } from "../landing/inviteCode";
import CashPrizeHero from "../landing/CashPrizeCard";

// The rail on a league's Home: the top of the board, and where you sit on it.
//
// One request feeds both cards — the full league leaderboard, same call the
// Leaderboard screen makes — so the rank shown here can't disagree with the rank
// shown there.
const TOP_N = 3;

const pct = (value) => {
  const n = Number(value ?? 0);
  if (!Number.isFinite(n)) return { text: "0.00%", tone: "text-secondary" };
  if (n > 0) return { text: `+${n.toFixed(2)}%`, tone: "text-[#22A36B]" };
  if (n < 0) return { text: `${n.toFixed(2)}%`, tone: "text-red-500" };
  return { text: "0.00%", tone: "text-secondary" };
};

const ordinal = (n) => {
  const rest = n % 100;
  if (rest >= 11 && rest <= 13) return `${n}th`;
  const suffix = ["th", "st", "nd", "rd"][n % 10] || "th";
  return `${n}${n % 10 <= 3 ? suffix : "th"}`;
};

const Avatar = ({ src, name, className = "w-7 h-7" }) =>
  src ? (
    <img
      src={src}
      alt=""
      className={`${className} rounded-full object-cover shrink-0 bg-[#F3F4F6]`}
      onError={(e) => {
        e.currentTarget.style.visibility = "hidden";
      }}
    />
  ) : (
    <span
      className={`${className} rounded-full bg-[#F3F4F6] flex items-center justify-center shrink-0`}
      title={name}
    >
      <AvatarPersonIcon className="w-4 h-4" />
    </span>
  );

const Card = ({ children, className = "" }) => (
  <div className={`rounded-2xl border border-gray-200 bg-white p-5 ${className}`}>{children}</div>
);

const LeagueRankRail = ({ game }) => {
  const gameId = game?.id;
  const slug = game?.vanity_url;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!gameId) return undefined;
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    (async () => {
      try {
        const res = await getLeaderBoard(gameId);
        if (cancelled) return;
        setRows(res?.data?.data?.data ?? []);
      } catch (e) {
        if (!cancelled) {
          console.error("League leaderboard load failed:", e);
          setFailed(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [gameId]);

  // Who "you" are, so the board can mark your row and My Rank has something to
  // measure. The leaderboard rows carry the user id under either key.
  const myId = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("userInfo") || "null")?.id ?? null;
    } catch (e) {
      return null;
    }
  }, []);

  const myIndex = rows.findIndex((r) => String(r.id ?? r.user_id) === String(myId));
  const me = myIndex >= 0 ? rows[myIndex] : null;
  // The gap to the person above you — the only number that tells you what to do
  // next. Nothing is shown for the leader, who has no one to chase.
  const ahead = myIndex > 0 ? rows[myIndex - 1] : null;
  const gap =
    ahead && me ? Number(ahead.performance ?? 0) - Number(me.performance ?? 0) : null;

  // A CASH GAME's prize belongs at the top of this rail. Client: "We need to
  // bring the prize card into the competition home page… Prize card becomes the
  // first card in the right column."
  const isCashGame = !!game?.is_cash_prize_league;

  // Client: "Prize card and Leaderboard card are sticky. Game details scrolls
  // beneath them."
  //
  // So the STICKY THING is the pair, not the rail. The rail itself is a plain
  // column now and the two cards that must stay on screen are pinned together
  // inside it; Game details follows in normal flow and travels up underneath
  // them as the page scrolls. `z-10` plus an opaque ground is what makes
  // "beneath" true rather than "over the top of" — a later sibling paints above
  // an earlier one by default, so without it the details card would slide over
  // the standings.
  //
  // The rail was one sticky <aside> before, which pinned all three cards as a
  // block; adding the prize card to that made the block taller than a
  // laptop-height window and stranded the bottom of Game details off-screen with
  // no way to reach it. Pinning only the pair leaves the third card free to
  // scroll, which is what the client asked to verify at ~800px.
  return (
    <div>
      {/* ── The prize, first in the rail ──────────────────────────────────
          Client: "Prize card content, stripped hero only: pill + status (top
          row), WINNER TAKES ALL, $100, REAL CASH. No eligible-assets or ends
          rows in this card, no contest rules. Same width as the Leaderboard and
          Game details cards."

          `payouts={false}` is the "stripped hero only" — the landing page's
          2nd/3rd payout box does not come with it. Everything else is the same
          CashPrizeHero the landing page and the details preview render, so the
          three screens cannot state a prize three different ways.

          The width is the rail's, by construction: this is the rail's own Card,
          the one the Leaderboard and Game details cards use.

          Cash games only. A public or private league has no prize card and this
          rail is unchanged for them. */}
      <div className="space-y-4 bg-white lg:sticky lg:top-24 lg:z-10 lg:pb-4">
      {isCashGame && (
        <Card>
          <CashPrizeHero game={game} payouts={false} />
        </Card>
      )}

      {/* Standings, edge to edge: rows run the full width of the card with their
          own hairlines, and the line about where you stand sits on a tinted strip
          at the foot. That is what makes it read as a board rather than a list
          with a caption. */}
      <Card className="!p-0">
        {/* Client Rev 4, DESKTOP: "Can we make the entire leaderboard header area
            of the card clickable, rather than just the chevron? From the divider
            line up. Just the top header block of the card. Not the leaderboard
            rankings."
            So this block IS the link now — podium, "Leaderboard", "See where you
            rank." and the chevron all inside one <Link>, ending exactly at the
            hairline that starts the standings. The rows below stay their own
            elements and are untouched.
            The chevron went from a <Link> to a plain <span>: an <a> inside an <a>
            is invalid HTML, and the browser closes the outer one at the inner
            tag, which would have made only the chevron clickable again — the very
            bug being fixed. */}
        <Link
          to={`/${slug}/leaderboard`}
          aria-label="Open the full leaderboard"
          className="flex items-start justify-between gap-3 px-5 pt-5 pb-3 transition-colors hover:bg-[#F8FAFA] focus-visible:bg-[#F8FAFA] rounded-t-2xl"
        >
          {/* Client Rev 3, desktop private league: "I do not see the leaderboard
              image in the leaderboard card on the home screen. It was added on
              mobile but not on desktop. Please add it on desktop."
              The mobile card (LeagueHome) leads with the client's own podium
              asset at w-11 h-11; this is that same file at that same size, so the
              two cards carry one image rather than two treatments of it. */}
          <div className="flex min-w-0 items-start gap-3">
            <img
              src="/podium.png"
              alt=""
              className="w-11 h-11 object-contain shrink-0"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
            <div className="min-w-0">
              <h3 className="font-montserrat font-bold text-base text-[#1A1A1A]">
                Leaderboard
              </h3>
              <p className="mt-0.5 text-xs text-secondary">See where you rank.</p>
            </div>
          </div>
          <span className="shrink-0 text-[#0F4146]" aria-hidden="true">
            <ChevronRightIcon className="w-5 h-5" />
          </span>
        </Link>

        <div className="divide-y divide-divider border-t border-divider">
          {loading ? (
            <p className="px-5 py-4 text-xs text-secondary">Loading standings…</p>
          ) : failed ? (
            <p className="px-5 py-4 text-xs text-secondary">
              Standings couldn't be loaded.
            </p>
          ) : rows.length === 0 ? (
            <p className="px-5 py-4 text-xs text-secondary">
              No standings yet. They appear once members start trading.
            </p>
          ) : (
            rows.slice(0, TOP_N).map((row, i) => {
              const isMe = String(row.id ?? row.user_id) === String(myId);
              const value = pct(row.performance);
              return (
                <div
                  key={row.id ?? row.user_id ?? i}
                  className={`flex items-center gap-3 px-5 py-3 ${
                    isMe ? "bg-[#F8FAFA]" : ""
                  }`}
                >
                  <span className="w-3 shrink-0 text-sm font-medium text-[#0F172A] tabular-nums">
                    {i + 1}
                  </span>
                  <Avatar src={row.image} name={row.user_name} className="w-8 h-8" />
                  {/* Client Rev 5: "Remove the you from the leaderboard. it is
                      not needed." The row is already marked as yours by its own
                      highlight, so the label was saying the same thing twice.
                      `isMe` still drives that highlight — only the text is
                      gone. */}
                  <span className="flex-1 min-w-0 truncate text-sm font-bold text-[#0F172A]">
                    {row.user_name || "—"}
                  </span>
                  <span className={`shrink-0 text-sm font-bold ${value.tone}`}>
                    {value.text}
                  </span>
                </div>
              );
            })
          )}
        </div>

        {/* Where you stand, on the card's own footing. It says only the thing
            worth acting on: the gap to the person above you. */}
        {me && (
          <p className="border-t border-divider bg-[#F8FAFA] px-5 py-3 text-center text-xs text-secondary">
            {gap !== null && gap > 0
              ? `${ahead.user_name || "The player above"} is +${gap.toFixed(1)}% ahead of you`
              : myIndex === 0
              ? competitionCopy(game).leadingLine
              : `You're ${ordinal(myIndex + 1)} — ${pct(me.performance).text}`}
          </p>
        )}
      </Card>

      </div>

      {/* ONE details card, both league types. Client Rev 2, desktop Private
          League Home 6: "League details right rail card should use the same
          component and visual treatment as Public League Home. Only the data
          changes. Do not invent a different private league details design."
          It also fixes 2 ("Move organizer avatar + Created by into the main
          league header... Remove it from the right rail League details card") —
          the organiser block lived only in the private card, which is gone.
          The row order the client listed (LEAGUE TYPE, ELIGIBLE ASSETS,
          PORTFOLIO VISIBILITY, MEMBERS, JOIN AFTER START) was already this
          card's order, so nothing moved then. It has since gained STARTS and
          ENDS after ELIGIBLE ASSETS, again at the client's own ordering. */}
      <div className="mt-4">
        <LeagueDetailsCard game={game} />
      </div>
    </div>
  );
};

// The league's own facts, in the rail beside the feed: what kind it is, what it
// trades, who can see what. The full version is the League details screen — this
// is the glance you get without leaving Home.
const DetailRow = ({ label, value }) =>
  value ? (
    <div className="py-2.5">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-secondary">
        {label}
      </p>
      <p className="mt-0.5 text-sm font-medium text-[#0F172A]">{value}</p>
    </div>
  ) : null;

const LeagueDetailsCard = ({ game }) => {
  // Client 0014889 renamed seven of these rows for a Cash Game. They come from
  // the shared map so this card and the Cash Game landing page can never end up
  // describing the same setting with two different words.
  const words = competitionCopy(game);
  const isCash = words.kind === "CASH GAME";

  return (
  <Card className="!p-0">
    {/* Section header, site-wide rule: Montserrat near-black. Only the screen
        title — the league's name — takes the dark-teal token. */}
    <div className="flex items-center gap-2 border-b border-divider px-5 py-4">
      <InfoCircleIcon className="w-5 h-5" stroke="#1A1A1A" />
      <h3 className="font-montserrat font-bold text-base text-[#1A1A1A]">
        {words.details}
      </h3>
    </div>

    <div className="divide-y divide-divider px-5 py-1">
      <DetailRow label={words.typeRowLabel} value={leagueTypeShort(game)} />
      <DetailRow label="Eligible assets" value={assetLabel(game) || assetTypeLabel(game)} />
      {/* Client: "Keep the dynamic countdown in the top identity header where it
          is; no change, but add the exact schedule inside the Game details or
          League details section. Use two separate rows: STARTS / ENDS."

          The countdown in the header is untouched — it answers how long is left.
          These answer which day and what time, which a countdown cannot.

          Placed here because the client gave the row order themselves and it is
          this card's order with the two new rows dropped in after Eligible
          assets: Competition type, Eligible assets, Starts, Ends, Portfolio
          visibility, Entrants, Entry after start.

          Both come from leagueStartAt / leagueEndAt, which is the single
          definition of a competition's window — 8 AM to 8 PM ET for every kind.
          (A stock-only league briefly closed at 5 PM on Rev4; the client
          withdrew that, see leagueFormat.js.)

          These briefly used a longer format of their own. Client: "Keep us
          consistent always... everything should read like this." So they are the
          same startMomentLabel / endMomentLabel the landing pages use, and there
          is one date-and-time string in the product again. */}
      <DetailRow label="Starts" value={startMomentLabel(game)} />
      <DetailRow label="Ends" value={endMomentLabel(game)} />
      {/* Client: "We need to add the STARTING CASH and TRADE LIMIT to this
          details menu on the desktop version for all leagues and game types",
          with the order written out — COMPETITION TYPE, ELIGIBLE ASSETS, STARTS,
          ENDS, STARTING CASH, TRADE LIMIT, PORTFOLIO VISIBILITY, MEMBERS, JOIN
          AFTER START. So they go here, between Ends and Portfolio visibility,
          and nothing else moves.

          "for all leagues and game types" is why there is no cash/league branch:
          the two strings come from leagueFormat, the same pair the public and
          private landing pages print, so this rail cannot end up quoting a
          different starting balance from the page that sold the competition. */}
      <DetailRow label="Starting cash" value={startingCashLabel(game)} />
      <DetailRow label="Trade limit" value={tradeLimitLabel(game)} />
      <DetailRow
        label="Portfolio visibility"
        value={
          isCash
            ? portfolioVisibilityGameShort(!!game?.private_portfolio)
            : portfolioVisibilityShort(!!game?.private_portfolio)
        }
      />
      <DetailRow
        label={words.membersRowLabel}
        value={`${memberCount(game)} ${words.joinedSuffix}`}
      />
      <DetailRow
        label={words.joinAfterStartLabel}
        value={joinAfterStartShort(!!game?.join_after_start)}
      />
    </div>

    {/* The reassurance a member wants standing in a competition: what happens in
        here does not touch the record they built outside.

        Client Rev 6: "Change the text copy line that is wrapping the one word to:
        League results don't affect your main record. This should fit without
        wrapping." The rail is 5rem narrower than the line needed, so "record."
        fell to a second line on its own. This is not new copy — it is the string
        League Play home already shows on mobile (LeaguePlayLanding), so the two
        surfaces now say the same thing. NOTE that LeaguePlayLanding's DESKTOP
        line deliberately still reads "stay separate": the client asked for that
        one to be put back and it is not this card. */}
    <p className="border-t border-divider px-5 py-4 text-center text-xs text-secondary">
      {words.recordLine}
    </p>
    </Card>
  );
};

export default LeagueRankRail;
